import type {LucideIcon} from "lucide-react";
import type {ReactNode} from "react";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {cn} from "@/lib/utils";
import {AdminEditModal} from "./AdminEditModal";

export type AdminTab = {
  id: string;
  label: ReactNode;
  content: ReactNode;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  headerExtra?: ReactNode;
  tabs: AdminTab[];
  defaultTab?: string;
  /** Controlled active tab */
  tab?: string;
  onTabChange?: (tab: string) => void;
  footer?: ReactNode;
  size?: "default" | "lg" | "xl" | "full";
};

export function AdminTabbedModal({
  open,
  onOpenChange,
  title,
  description,
  icon,
  headerExtra,
  tabs,
  defaultTab,
  tab,
  onTabChange,
  footer,
  size = "xl"
}: Props) {
  const first = defaultTab || tabs[0]?.id;
  const tabsProps = tab != null
    ? {value: tab, onValueChange: onTabChange}
    : {defaultValue: first};

  return (
    <AdminEditModal open={open} onOpenChange={onOpenChange} title={title} description={description} icon={icon} headerExtra={headerExtra} footer={footer} size={size}>
      <Tabs {...tabsProps} className="admin-tabs w-full">
        <div className="admin-tabs__bar sticky top-0 z-10 -mx-5 mb-5 sm:-mx-6">
          <TabsList className="admin-tabs__list flex h-auto w-full justify-start gap-0 overflow-x-auto rounded-none bg-transparent p-0">
            {tabs.map((t) => (
              <TabsTrigger
                key={t.id}
                value={t.id}
                className={cn(
                  "admin-tabs__trigger relative shrink-0 rounded-none border-0 bg-transparent px-3.5 py-3.5 text-sm font-medium text-muted-foreground shadow-none",
                  "hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none",
                  "sm:px-4"
                )}
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {tabs.map((t) => (
          <TabsContent key={t.id} value={t.id} className="admin-tabs__panel mt-0 focus-visible:outline-none">
            {t.content}
          </TabsContent>
        ))}
      </Tabs>
    </AdminEditModal>
  );
}
